"use client"

import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface DatePickerProps {
  value?: Date
  onChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  minDate?: Date
  maxDate?: Date
  allowClear?: boolean
  className?: string
}

const DatePicker = React.forwardRef<HTMLButtonElement, DatePickerProps>(
  ({ className, value, onChange, placeholder = "Pick a date", disabled, minDate, maxDate, allowClear = false, ...props }, ref) => {
    const [open, setOpen] = React.useState(false)
    const popoverContentRef = React.useRef<HTMLDivElement>(null)
    const selectInteractionRef = React.useRef(false)

    // Track select interactions
    React.useEffect(() => {
      if (!open || !popoverContentRef.current) return

      const popoverContent = popoverContentRef.current
      const selectElements = popoverContent.querySelectorAll('select')

      const handleSelectMouseDown = (e: MouseEvent) => {
        e.stopPropagation()
        selectInteractionRef.current = true
        // Keep flag true for a longer time to allow dropdown to fully open and interact
        setTimeout(() => {
          const activeEl = document.activeElement as HTMLElement
          // Only clear if select is no longer focused
          if (!activeEl || activeEl.tagName !== 'SELECT' || !popoverContent.contains(activeEl)) {
            selectInteractionRef.current = false
          }
        }, 1000)
      }

      const handleSelectClick = (e: MouseEvent) => {
        e.stopPropagation()
        selectInteractionRef.current = true
        setTimeout(() => {
          const activeEl = document.activeElement as HTMLElement
          if (!activeEl || activeEl.tagName !== 'SELECT' || !popoverContent.contains(activeEl)) {
            selectInteractionRef.current = false
          }
        }, 1000)
      }

      const handleSelectFocus = () => {
        selectInteractionRef.current = true
      }

      const handleSelectBlur = () => {
        // Delay clearing to allow dropdown interactions
        setTimeout(() => {
          const activeEl = document.activeElement as HTMLElement
          if (!activeEl || activeEl.tagName !== 'SELECT' || !popoverContent.contains(activeEl)) {
            selectInteractionRef.current = false
          } else {
            // Still focused on a select, keep flag true
            selectInteractionRef.current = true
          }
        }, 300)
      }

      selectElements.forEach((select) => {
        select.addEventListener('mousedown', handleSelectMouseDown, true)
        select.addEventListener('click', handleSelectClick, true)
        select.addEventListener('focus', handleSelectFocus, true)
        select.addEventListener('blur', handleSelectBlur, true)
      })

      return () => {
        selectElements.forEach((select) => {
          select.removeEventListener('mousedown', handleSelectMouseDown, true)
          select.removeEventListener('click', handleSelectClick, true)
          select.removeEventListener('focus', handleSelectFocus, true)
          select.removeEventListener('blur', handleSelectBlur, true)
        })
        selectInteractionRef.current = false
      }
    }, [open])

    const handleSelect = (date: Date | undefined) => {
      if (!date) {
        if (allowClear) {
          onChange?.(undefined)
          setOpen(false)
        }
        return
      }
      onChange?.(date)
      setOpen(false) // Close popover after selecting a date
    }

    const handleClear = () => {
      onChange?.(undefined)
    }

    const handleInteractOutside = (event: Event) => {
      const target = event.target as HTMLElement
      const activeElement = document.activeElement as HTMLElement
      const popoverContent = popoverContentRef.current
      
      if (!popoverContent) return
      
      // If we're currently interacting with a select, prevent closing
      if (selectInteractionRef.current) {
        event.preventDefault()
        return
      }
      
      // Always prevent closing if target is within popover content
      if (popoverContent.contains(target)) {
        event.preventDefault()
        return
      }
      
      // Check if active element (focused select or any element) is within popover content
      if (activeElement && popoverContent.contains(activeElement)) {
        event.preventDefault()
        return
      }
      
      // Prevent closing when interacting with native select dropdowns
      // Native select options render outside the DOM tree, so we check the active element
      if (activeElement && activeElement.tagName === 'SELECT' && popoverContent.contains(activeElement)) {
        event.preventDefault()
        return
      }
      
      // Check if the interaction is with a select element that's inside the popover
      if (target.tagName === 'SELECT' || target.closest('select')) {
        const selectElement = target.tagName === 'SELECT' ? target : target.closest('select')
        if (selectElement && popoverContent.contains(selectElement as Node)) {
          event.preventDefault()
          return
        }
      }
      
      // Check if target is part of the calendar (react-day-picker elements)
      if (target.closest('[data-slot="calendar"]') || target.closest('.rdp')) {
        event.preventDefault()
        return
      }
    }
    
    const handlePointerDownOutside = (event: Event) => {
      const target = event.target as HTMLElement
      const activeElement = document.activeElement as HTMLElement
      const popoverContent = popoverContentRef.current
      
      if (!popoverContent) return
      
      // If we're currently interacting with a select, prevent closing
      if (selectInteractionRef.current) {
        event.preventDefault()
        return
      }
      
      // Always prevent closing if target is within popover content
      if (popoverContent.contains(target)) {
        event.preventDefault()
        return
      }
      
      // Check if active element is within popover content
      if (activeElement && popoverContent.contains(activeElement)) {
        event.preventDefault()
        return
      }
      
      // Prevent closing when clicking on select elements
      if (target.tagName === 'SELECT' || target.closest('select')) {
        const selectElement = target.tagName === 'SELECT' ? target : target.closest('select')
        if (selectElement && popoverContent.contains(selectElement as Node)) {
          event.preventDefault()
          return
        }
      }
      
      // Check if target is part of the calendar
      if (target.closest('[data-slot="calendar"]') || target.closest('.rdp')) {
        event.preventDefault()
        return
      }
    }

    return (
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant={"outline"}
            type="button"
            className={cn(
              "w-[240px] justify-start text-left font-normal",
              !value && "text-muted-foreground",
              className
            )}
            disabled={disabled}
            {...props}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? (
              format(value, "PPP")
            ) : (
              <span>{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          ref={popoverContentRef}
          className="w-auto p-0 pointer-events-auto z-[150]" 
          align="start"
          onInteractOutside={handleInteractOutside}
          onPointerDownOutside={handlePointerDownOutside}
          onEscapeKeyDown={() => {
            // Allow escape to close
            setOpen(false)
          }}
        >
          <div 
            className="pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <Calendar
              mode="single"
              selected={value}
              onSelect={handleSelect}
              disabled={(date) => {
                if (disabled) return true
                if (maxDate && date > maxDate) return true
                if (minDate && date < minDate) return true
                return false
              }}
              captionLayout="dropdown"
              initialFocus
            />
          </div>
          {allowClear && value && (
            <div className="p-3 border-t">
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={handleClear}
                className="w-full"
              >
                Clear
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    )
  }
)

DatePicker.displayName = "DatePicker"

export { DatePicker }
export type { DatePickerProps }
